import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from "@angular/core";
import { NzModalService } from "ng-zorro-antd/modal";
import { NzTableSortOrder } from "ng-zorro-antd/table";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { TranslateService } from "@ngx-translate/core";
import { Clipboard } from "@angular/cdk/clipboard";
import { distinctUntilChanged, filter, Subject, take, takeUntil } from "rxjs";
import { TokenService } from "../../../../api/token/token.service";
import { AgentTokenInfo } from "../../../../api/token/token.types";
import { AdminApiTokenRevealComponent } from "./admin-api-token-reveal/admin-api-token-reveal.component";
import { AuthorizationService } from "../../../../api/authorization/authorization.service";
import { AccountType } from "../../../login-page/login-page-data/login.types";

/**
 * A failed load has to stay apart from "there is nothing". For the personal token it decides whether
 * generating would invalidate one that is still in use; for the overview it decides whether the page may
 * claim that no agent token exists.
 */
type LoadStatus = "loading" | "loaded" | "failed";

/**
 * Agent tokens are the API keys handed to an agentic harness (langdock). Every admin manages their own
 * token here, while the overview of all tokens is reserved for admins of type ADMIN. The backend filters
 * the overview by token type, so it just renders what it gets.
 */
@Component({
  selector: "mh-admin-api-tokens",
  templateUrl: "./admin-api-tokens.component.html",
  styleUrls: ["./admin-api-tokens.component.less"],
})
export class AdminApiTokensComponent implements OnInit, OnDestroy {
  /** The clear text value never lives here - it only ever exists inside the reveal dialog. */
  myToken: string | null = null;
  myTokenStatus: LoadStatus = "loading";

  /**
   * Only one token mutation may run at a time: a revoke landing after a generate would silently kill the
   * token the reveal dialog has just handed out as valid.
   */
  private mutation: "generate" | "revoke" | null = null;

  tokens: Array<AgentTokenInfo> = [];
  tokensStatus: LoadStatus = "loading";

  /** Identifier of the token a revoke confirmation is currently asking about. */
  revokeTarget = "";

  /** Whether the current admin user is allowed to view and manage all tokens (only ADMIN type). */
  canViewAllTokens = false;

  private readonly destroy$ = new Subject<void>();

  @ViewChild("revokeConfirmContent") private revokeConfirmContent!: TemplateRef<void>;

  /**
   * The backend masks everything but the last few characters, which for an encrypted token means close to
   * 200 asterisks - enough to stretch the page sideways - while carrying no information.
   */
  private static readonly MASK_LENGTH = 8;

  get generating(): boolean {
    return this.mutation === "generate";
  }

  get revoking(): boolean {
    return this.mutation === "revoke";
  }

  get mutating(): boolean {
    return this.mutation !== null;
  }

  get myTokenLoading(): boolean {
    return this.myTokenStatus === "loading";
  }

  get loading(): boolean {
    return this.tokensStatus === "loading";
  }

  get tokensFailed(): boolean {
    return this.tokensStatus === "failed";
  }

  get myTokenFailed(): boolean {
    return this.myTokenStatus === "failed";
  }

  get hasNoToken(): boolean {
    return this.myTokenStatus === "loaded" && !this.myToken;
  }

  get canGenerate(): boolean {
    return this.myTokenStatus === "loaded" && !this.mutating;
  }

  constructor(
    private readonly tokenService: TokenService,
    private readonly modal: NzModalService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly notificationService: NzNotificationService,
    private readonly translateService: TranslateService,
    private readonly clipboard: Clipboard,
    private readonly authorizationService: AuthorizationService,
  ) {}

  ngOnInit(): void {
    this.fetchMyToken();
    this.authorizationService
      .getAccountType()
      .pipe(
        filter((type): type is AccountType => !!type),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((accountType) => {
        this.canViewAllTokens = accountType === AccountType.ADMIN;
        this.fetchAllTokens();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchMyToken(): void {
    this.myTokenStatus = "loading";
    this.tokenService
      .getMyAgentToken()
      .pipe(take(1))
      .subscribe({
        next: (token) => {
          this.myToken = token;
          this.myTokenStatus = "loaded";
        },
        error: () => {
          this.myToken = null;
          this.myTokenStatus = "failed";
        },
      });
  }

  fetchAllTokens(): void {
    if (!this.canViewAllTokens) {
      return;
    }

    this.tokensStatus = "loading";
    this.tokenService
      .getAllAgentTokens()
      .pipe(take(1))
      .subscribe({
        next: (tokens) => {
          this.tokens = tokens;
          this.tokensStatus = "loaded";
        },
        error: () => {
          this.tokens = [];
          this.tokensStatus = "failed";
        },
      });
  }

  /** Regenerating invalidates the previous token, so anything already using it stops working. */
  onGenerate(): void {
    if (!this.canGenerate) {
      return;
    }

    if (!this.myToken) {
      this.generateToken();
      return;
    }

    this.modal.confirm({
      nzTitle: this.translateService.instant("admin.api-tokens.regenerate.confirm.title"),
      nzContent: this.translateService.instant("admin.api-tokens.regenerate.confirm.text"),
      nzOkText: this.translateService.instant("admin.api-tokens.regenerate.confirm.ok.btn"),
      nzCancelText: this.translateService.instant("admin.api-tokens.confirm.cancel.btn"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.generateToken(),
    });
  }

  onRevokeMyToken(): void {
    this.modal.confirm({
      nzTitle: this.translateService.instant("admin.api-tokens.revoke.confirm.title"),
      nzContent: this.translateService.instant("admin.api-tokens.revoke.confirm.text"),
      nzOkText: this.translateService.instant("admin.api-tokens.revoke.confirm.ok.btn"),
      nzCancelText: this.translateService.instant("admin.api-tokens.confirm.cancel.btn"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => {
        this.mutation = "revoke";
        this.tokenService
          .removeMyAgentToken()
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.mutation = null;
              this.notificationService.success("", this.translateService.instant("admin.api-tokens.revoke.success"));
              this.myToken = null;
              this.fetchAllTokens();
            },
            error: () => (this.mutation = null),
          });
      },
    });
  }

  onRevokeToken(token: AgentTokenInfo): void {
    this.revokeTarget = token.email ?? token.accountId;

    this.modal.confirm({
      nzTitle: this.translateService.instant("admin.api-tokens.revoke.confirm.title"),
      nzContent: this.revokeConfirmContent,
      nzOkText: this.translateService.instant("admin.api-tokens.revoke.confirm.ok.btn"),
      nzCancelText: this.translateService.instant("admin.api-tokens.confirm.cancel.btn"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => {
        this.mutation = "revoke";
        this.tokenService
          .removeAgentTokenByAccount(token.accountId)
          .pipe(take(1))
          .subscribe({
            next: () => {
              this.mutation = null;
              this.notificationService.success("", this.translateService.instant("admin.api-tokens.revoke.success"));
              this.tokens = this.tokens.filter((item) => item.accountId !== token.accountId);
              this.fetchMyToken();
            },
            error: () => (this.mutation = null),
          });
      },
    });
  }

  shortenToken(obfuscatedToken: string | null): string {
    if (!obfuscatedToken) {
      return "";
    }
    const readableTail = obfuscatedToken.replace(/^\**/, "");
    return "*".repeat(AdminApiTokensComponent.MASK_LENGTH) + readableTail;
  }

  copyAccountId(accountId: string): void {
    this.copyToClipboard(accountId, "admin.copy-id.success");
  }

  private copyToClipboard(text: string, successKey: string): void {
    if (!text) {
      return;
    }

    if (this.clipboard.copy(text)) {
      this.notificationService.success("", this.translateService.instant(successKey));
    } else {
      this.notificationService.error("", this.translateService.instant("notification.error"));
    }
  }

  sortByEmail = (a: AgentTokenInfo, b: AgentTokenInfo, sortOrder?: NzTableSortOrder): number =>
    AdminApiTokensComponent.compareNullable(a.email, b.email, sortOrder, (first, second) =>
      first.localeCompare(second),
    );

  sortByCreated = (a: AgentTokenInfo, b: AgentTokenInfo, sortOrder?: NzTableSortOrder): number =>
    AdminApiTokensComponent.compareNullable(a.created, b.created, sortOrder, (first, second) => first - second);

  sortByLastUsed = (a: AgentTokenInfo, b: AgentTokenInfo, sortOrder?: NzTableSortOrder): number =>
    AdminApiTokensComponent.compareNullable(a.lastUsed, b.lastUsed, sortOrder, (first, second) => first - second);

  /**
   * Keeps rows without a value at the bottom in both directions: nz-table flips the sign of the result
   * for a descending sort, so the side the missing values belong on depends on the direction.
   */
  private static compareNullable<T>(
    a: T | null,
    b: T | null,
    sortOrder: NzTableSortOrder | undefined,
    compare: (first: T, second: T) => number,
  ): number {
    const missingLast = sortOrder === "descend" ? -1 : 1;

    if (a === null) {
      return b === null ? 0 : missingLast;
    }
    if (b === null) {
      return -missingLast;
    }
    return compare(a, b);
  }

  private generateToken(): void {
    this.mutation = "generate";
    this.tokenService
      .generateMyAgentToken()
      .pipe(take(1))
      .subscribe({
        next: (token) => {
          this.mutation = null;
          this.revealToken(token);
          this.fetchMyToken();
          this.fetchAllTokens();
        },
        error: () => (this.mutation = null),
      });
  }

  /** The clear text token can be read exactly once, so the dialog must not be dismissable by accident. */
  private revealToken(token: string): void {
    this.modal.create({
      nzContent: AdminApiTokenRevealComponent,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzClosable: false,
      nzFooter: null,
      nzWidth: 620,
      nzViewContainerRef: this.viewContainerRef,
      nzData: { token },
    });
  }
}
